import React, { useEffect, useState } from 'react';
import Cropper, { Area } from 'react-easy-crop';
import styles from './CropperOverlay.module.css';
import { Transformation } from '../../../../shared/types/imagesType';
import { commitHistory, updateSelectedImageSetting } from '../../../../store/slices/imageSlice';
import { useAppDispatch } from '../../../../store/store';

interface Props {
    imageData: any;
    onApply: (blob: Blob, transform: Transformation) => void;
    onCancel: () => void;
}

const ALLOWED_ASPECTS = new Set(['3:2', '1:1', '4:3', '16:9', '4:5']);

const aspectStringToNumber = (s?: string): number | undefined => {
    if (!s) return undefined;
    switch (s) {
        case '3:2':
            return 3 / 2;
        case '1:1':
            return 1;
        case '4:3':
            return 4 / 3;
        case '16:9':
            return 16 / 9;
        case '4:5':
            return 4 / 5;
        default:
            return undefined;
    }
};

const CropperOverlay: React.FC<Props> = ({ imageData, onApply, onCancel }) => {
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [flipX, setFlipX] = useState(false);
    const [flipY, setFlipY] = useState(false);
    const [aspect, setAspect] = useState<number | undefined>(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
    const [initialPixels, setInitialPixels] = useState<Area | undefined>(undefined);
    const dispatch = useAppDispatch();

    useEffect(() => {
        const t: Transformation | undefined = imageData?.transformation ?? undefined;
        if (!t) return;
        setRotation(Math.round(t.rotation ?? 0));
        const flipStr = (t.flip || 'none').toLowerCase();
        setFlipX(flipStr === 'horizontal');
        setFlipY(flipStr === 'vertical');
        setZoom(t?.crop?.zoom ?? 1);
        const arStr = t?.presets?.aspect_ratio;
        const arNum = ALLOWED_ASPECTS.has(arStr || '') ? aspectStringToNumber(arStr!) : undefined;
        setAspect(arNum ?? undefined);
        if (
            Number.isFinite(t?.crop?.x) &&
            Number.isFinite(t?.crop?.y) &&
            Number.isFinite(t?.crop?.width) &&
            Number.isFinite(t?.crop?.height)
        ) {
            setInitialPixels({
                x: Number(t.crop.x),
                y: Number(t.crop.y),
                width: Math.round(Number(t.crop.width)),
                height: Math.round(Number(t.crop.height)),
            });
        } else {
            setInitialPixels(undefined);
        }
    }, [imageData]);

    const onCropComplete = (_: Area, croppedPixels: Area) => {
        setCroppedAreaPixels(croppedPixels);
    };

    const decimalToRatio = (x: number, maxDen = 1000) => {
        if (!Number.isFinite(x) || x <= 0) return undefined;
        let bestNum = 1,
            bestDen = 1,
            minErr = Infinity;
        for (let den = 1; den <= maxDen; den++) {
            const num = Math.round(x * den);
            const err = Math.abs(x - num / den);
            if (err < minErr) {
                bestNum = num;
                bestDen = den;
                minErr = err;
            }
        }
        const gcd = (a: number, b: number): number => (b ? gcd(b, a % b) : Math.abs(a));
        const g = gcd(bestNum, bestDen);
        return `${bestNum / g}:${bestDen / g}`;
    };

    const handleApply = async () => {
        if (!croppedAreaPixels) return;

        let outRotation = ((Math.round(rotation) % 360) + 360) % 360;
        let flip: 'none' | 'horizontal' | 'vertical' = 'none';
        if (flipX && !flipY) flip = 'horizontal';
        else if (flipY && !flipX) flip = 'vertical';
        else if (flipX && flipY) {
            outRotation = (outRotation + 180) % 360;
            flip = 'none';
        }

        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = imageData.original_url || imageData.url;
        await new Promise((r) => (img.onload = r));
        const srcW = img.naturalWidth || img.width;
        const srcH = img.naturalHeight || img.height;

        const cropX = Math.round(croppedAreaPixels.x);
        const cropY = Math.round(croppedAreaPixels.y);
        const cropW = Math.round(croppedAreaPixels.width);
        const cropH = Math.round(croppedAreaPixels.height);

        const ratioMaybe = aspect ? decimalToRatio(aspect) : undefined;
        const safeAspectRatio =
            ratioMaybe && ALLOWED_ASPECTS.has(ratioMaybe) ? ratioMaybe : undefined;

        const transform: Transformation = {
            width: srcW,
            height: srcH,
            rotation: outRotation,
            flip,
            presets: {
                size: `${srcW}x${srcH}px`,
                aspect_ratio: safeAspectRatio,
            },
            x: cropX,
            y: cropY,
            crop: {
                x: cropX,
                y: cropY,
                width: cropW,
                height: cropH,
                zoom,
            },
        };

        dispatch(updateSelectedImageSetting({ key: 'transformation', value: transform }));
        dispatch(
            commitHistory({
                label: 'effect:intensity',
                opts: { groupId: 'effect:crop', replace: true },
            }),
        );

        onApply(new Blob([], { type: 'application/octet-stream' }), transform);
    };

    const aspectRatios = [
        { label: '3:2', value: 3 / 2 },
        { label: '1:1', value: 1 / 1 },
        { label: '4:3', value: 4 / 3 },
        { label: '16:9', value: 16 / 9 },
        { label: '4:5', value: 4 / 5 },
    ];

    return (
        <div className={styles.overlay}>
            <div className={styles.cropperWrapper}>
                <div
                    className={styles.cropperTransform}
                    style={{
                        transform: `scaleX(${flipX ? -1 : 1}) scaleY(${flipY ? -1 : 1})`,
                        backgroundColor: '#fff',
                    }}
                >
                    <Cropper
                        image={imageData.original_url || imageData.url}
                        crop={crop}
                        zoom={zoom}
                        rotation={rotation}
                        aspect={aspect}
                        onCropChange={setCrop}
                        onCropComplete={onCropComplete}
                        onZoomChange={setZoom}
                        onRotationChange={(r) => setRotation(Math.round(r))}
                        initialCroppedAreaPixels={initialPixels}
                    />
                </div>
            </div>

            <div className={styles.toolbar}>
                <div className={styles.toolbarRow}>
                    <div className={styles.controlGroup}>
                        <label className={styles.label}>
                            Zoom: <strong>{zoom.toFixed(1)}x</strong>
                        </label>
                        <input
                            type="range"
                            min={1}
                            max={3}
                            step={0.1}
                            value={zoom}
                            onChange={(e) => setZoom(Number(e.target.value))}
                            className={styles.customSlider}
                        />
                    </div>

                    <div className={styles.controlGroupWide}>
                        <label className={styles.label}>
                            Rotate: <strong>{rotation}°</strong>
                        </label>
                        <input
                            type="range"
                            min={0}
                            max={360}
                            step={1}
                            value={rotation}
                            onChange={(e) => setRotation(Math.round(Number(e.target.value)))}
                            className={styles.customSlider}
                        />
                    </div>

                    <button
                        className={styles.flipBtn}
                        onClick={() => {
                            setFlipY(false);
                            setFlipX((v) => !v);
                        }}
                    >
                        Flip X
                    </button>
                    <button
                        className={styles.flipBtn}
                        onClick={() => {
                            setFlipX(false);
                            setFlipY((v) => !v);
                        }}
                    >
                        Flip Y
                    </button>
                </div>

                <div className={styles.toolbarRow}>
                    <select
                        value={aspect}
                        onChange={(e) => setAspect(Number(e.target.value))}
                        className={styles.select}
                    >
                        {aspectRatios.map((r) => (
                            <option key={r.label} value={r.value}>
                                {r.label}
                            </option>
                        ))}
                    </select>

                    <div className={styles.buttonRow}>
                        <button className={styles.cancelBtn} onClick={onCancel}>
                            Cancel
                        </button>
                        <button className={styles.applyBtn} onClick={handleApply}>
                            Apply
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CropperOverlay;
